import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type BreakpointKey = 'mobile' | 'tablet' | 'desktop';

interface UnitsRect { col: number; row: number; w: number; h: number; }

interface GridContentContextValue {
  cellSize: number;
  columns: number;
  rows: number;
  rowsDelta: number;
  editMode: boolean;
  setEditMode: (on: boolean) => void;
  toGrid: (px: number) => number;
  toPx: (units: number) => number;
  boardRef: React.RefObject<HTMLDivElement | null>;
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  nextZIndex: () => number;
  breakpoint: BreakpointKey;
  debug: boolean;
  registerItem: (id: string, getLayout: () => Record<BreakpointKey, UnitsRect>, setLayoutFor: (bp: BreakpointKey, rect: UnitsRect) => void) => void;
  unregisterItem: (id: string) => void;
  saveLayoutDebounced: () => void;
  markDirty: () => void;
  saveAll: () => Promise<void>;
}

const GridContentContext = createContext<GridContentContextValue | null>(null);

export const useGridContent = (): GridContentContextValue => {
  const ctx = useContext(GridContentContext);
  if (!ctx) throw new Error('useGridContent must be used within GridContent');
  return ctx;
};

interface GridContentProps {
  children: React.ReactNode;
  className?: string;
  guide?: boolean; // enables guide visuals (alias: grid)
  grid?: boolean;  // alias for guide to keep legacy API
  cellSize?: number; // default 64
  autoHeight?: boolean; // automatically calculate height based on content
  minRows?: { desktop?: number; tablet?: number; mobile?: number }; // override autoHeight with specific row counts
  layoutPath?: string; // relative to src/, e.g., 'components/HomePage/layout'
  /** When true, board fills container width instead of exact grid width */
  fullBleed?: boolean;
  /** When true, removes default horizontal padding */
  noPadding?: boolean;
}

function getBreakpointKey(w: number): BreakpointKey {
  // Allow debug override via global flag
  try {
    if (typeof window !== 'undefined' && (window as any).__debugBP) {
      const v = String((window as any).__debugBP).toLowerCase();
      if (v === 'mobile' || v === 'tablet' || v === 'desktop') return v as BreakpointKey;
    }
  } catch {}
  // Device-based breakpoints (standard device sizes):
  // Mobile: ≤ 600px | Tablet: 601-1024px | Desktop: ≥ 1025px
  // Grid adapts to these by accounting for 64px margins on each side (128px total)
  if (w <= 600) return 'mobile';     // 600px and below (small devices)
  if (w <= 1024) return 'tablet';    // 601px - 1024px (medium devices)
  return 'desktop';                   // 1025px and above (large devices)
}

const GridContent: React.FC<GridContentProps> = ({
  children,
  className = '',
  guide = false,
  grid = false,
  cellSize = 64,
  autoHeight = false,
  minRows,
  layoutPath,
  fullBleed = false,
  noPadding = false
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  // guide controls debug, but editMode can be controlled independently
  const enableGuide = guide || grid;
  const [editMode, setEditMode] = useState<boolean>(enableGuide);
  const showGuides = enableGuide && editMode;
  const debug = showGuides;
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(typeof window === 'undefined' ? 1280 : window.innerWidth);
  const [viewportHeight, setViewportHeight] = useState<number>(typeof window === 'undefined' ? 800 : window.innerHeight);
  
  // Responsive cellSize: calculate dynamically for mobile & tablet with fixed margins
  // Mobile (≤600px): 5 fixed columns, cell scales to fit
  // Tablet (601-1024px): 9 or 13 fixed columns, cell scales to fit
  // Desktop (≥1025px): fixed 64px cells, columns vary
  const tabletColumns = viewportWidth <= 812 ? 10 : 13;
  const responsiveCellSize = useMemo(() => {
    if (viewportWidth <= 600) {
      // Very small screens (≤320px): 20px margins each side = 40px total
      // Regular mobile (321-600px): 32px margins each side = 64px total
      const mobileMarginTotal = viewportWidth <= 320 ? 40 : 64;
      return Math.floor((viewportWidth - mobileMarginTotal) / 5);
    }
    if (viewportWidth <= 1024) {
      // Tablet: 64px margins each side (128px total), fixed column count
      const tabletMarginTotal = 128;
      return Math.floor((viewportWidth - tabletMarginTotal) / tabletColumns);
    }
    return cellSize;
  }, [viewportWidth, cellSize, tabletColumns]);
  const [zCounter, setZCounter] = useState<number>(10);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [breakpoint, setBreakpoint] = useState<BreakpointKey>(() => getBreakpointKey(typeof window === 'undefined' ? 1280 : window.innerWidth));
  const [rowsDelta, setRowsDelta] = useState<number>(0);
  const [dirty, setDirty] = useState<boolean>(false); // retained for future actions; currently hidden from panel
  const [panelMounted, setPanelMounted] = useState<boolean>(false);
  const [panelPos, setPanelPos] = useState<{ x: number; y: number }>({ x: 12, y: 12 });
  const panelRef = useRef<HTMLDivElement>(null);
  const panelDragging = useRef<boolean>(false);
  const panelPointerId = useRef<number | null>(null);
  const panelStart = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [isPanelOwner, setIsPanelOwner] = useState<boolean>(false);
  const instanceIdRef = useRef<string>('');
  if (!instanceIdRef.current) instanceIdRef.current = Math.random().toString(36).slice(2);
  // Bump when column-based overrides load so autoHeight rows recompute
  const [layoutTick, setLayoutTick] = useState<number>(0);

  // Registry of items to collect and apply layouts
  const registryRef = useRef<Map<string, { getLayout: () => Record<BreakpointKey, UnitsRect>; setLayoutFor: (bp: BreakpointKey, rect: UnitsRect) => void }>>(new Map());
  const pendingOverridesRef = useRef<Record<BreakpointKey, Record<string, UnitsRect>>>({ mobile: {}, tablet: {}, desktop: {} });
  const saveTimerRef = useRef<number | null>(null);

  // Track if edit mode has been manually changed by user
  const userChangedEditMode = useRef(false);
  
  // Only initialize edit mode from guide prop on mount, don't keep forcing it
  // This allows users to toggle edit mode independently after initial load
  useEffect(() => { 
    // Only set editMode from guide if user hasn't manually changed it
    if (!userChangedEditMode.current) {
      setEditMode(enableGuide);
    }
  }, [enableGuide]);

  // Create a wrapped setEditMode that tracks user changes
  const wrappedSetEditMode = useCallback((value: boolean) => {
    userChangedEditMode.current = true;
    setEditMode(value);
  }, []);

  // Add keyboard shortcut to toggle edit mode (E key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only respond to 'E' key when not typing in an input/textarea
      if (e.key.toLowerCase() === 'e' && 
          !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName) &&
          !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        wrappedSetEditMode(!editMode);
        if (debug) {
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, wrappedSetEditMode, debug]);

  // Measure container size and viewport
  useEffect(() => {
    setPanelMounted(true);
    if (!boardRef.current) return;

    const updateSize = () => {
      if (!boardRef.current) return;
      // Batch all layout reads together before any state updates
      const newWidth = boardRef.current.clientWidth;
      const newHeight = boardRef.current.clientHeight;
      const newViewportWidth = window.innerWidth;
      const newViewportHeight = window.innerHeight;

      // Force update even if values seem the same to ensure recalculation
      setContainerWidth(newWidth);
      setContainerHeight(newHeight);
      setViewportWidth(newViewportWidth);
      setViewportHeight(newViewportHeight);

    if (debug) {
        // Debug info removed
      }
    };

    updateSize();
    const ro = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure DOM has updated and batch layout reads
      requestAnimationFrame(updateSize);
    });
    ro.observe(boardRef.current);

    const onResize = () => {
      // Batch all layout reads together before any state updates to avoid forced reflow
      const newViewportWidth = window.innerWidth;
      const newViewportHeight = window.innerHeight;
      const newBreakpoint = getBreakpointKey(newViewportWidth);
      const board = boardRef.current;
      const newContainerWidth = board ? board.clientWidth : 0;
      const newContainerHeight = board ? board.clientHeight : 0;

      // Update all state together after all reads are complete (no forced reflow)
      setBreakpoint(newBreakpoint);
      setViewportWidth(newViewportWidth);
      setViewportHeight(newViewportHeight);
      setContainerWidth(newContainerWidth);
      setContainerHeight(newContainerHeight);

      if (debug) {
        // Debug info removed
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [debug]);

  // Global singleton ownership of the floating panel to prevent duplicates
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    if (!showGuides) {
      if (isPanelOwner && w.__gridToolsOwner === instanceIdRef.current) {
        delete w.__gridToolsOwner;
      }
      setIsPanelOwner(false);
      return;
    }
    if (!w.__gridToolsOwner) {
      w.__gridToolsOwner = instanceIdRef.current;
      setIsPanelOwner(true);
    } else {
      setIsPanelOwner(w.__gridToolsOwner === instanceIdRef.current);
    }
    return () => {
      if (w.__gridToolsOwner === instanceIdRef.current) delete w.__gridToolsOwner;
    };
  }, [showGuides, isPanelOwner]);

  const columns = useMemo(() => {
    // Responsive column count:
    // Mobile (≤600px): Always 5 columns with dynamic margins (20px each side for ≤320px, 32px each side for 321-600px)
    // Tablet (601-1024px): 7-13 columns with 64px margins each side (128px total)
    // Desktop (≥1025px): 14-20 columns with 64px margins each side (128px total - 20 columns x 64px = 1280px max grid width)
    
    // Mobile: fixed 5 columns always
    if (breakpoint === 'mobile') {
      return 5;
    }
    
    // Tablet: fixed 9 or 13 columns (cell size scales to fit)
    if (breakpoint === 'tablet') {
      return tabletColumns;
    }

    // Desktop: 14-20 columns with fixed 64px cells
    const marginTotal = 128; // 64px on each side
    const availableWidth = viewportWidth - marginTotal;
    const computed = Math.floor(availableWidth / responsiveCellSize);
    if (computed <= 0) return 14;
    return Math.max(14, Math.min(20, computed));
  }, [viewportWidth, responsiveCellSize, breakpoint]);

  // Calculate content-based rows by scanning children and/or column overrides
  const contentBasedRows = useMemo(() => {
    if (!autoHeight) return null;
    let maxRow = 1;

    // Prefer column-based overrides when using /layouts
    const isColumnBased = layoutPath?.endsWith('/layouts');
    let hasColumnOverrides = false;
    if (isColumnBased) {
      try {
        const overrides = pendingOverridesRef.current[breakpoint] || {};
        const keys = Object.keys(overrides);
        if (keys.length > 0) hasColumnOverrides = true;
        for (const id of keys) {
          const u = overrides[id];
          if (u && typeof u.row === 'number' && typeof u.h === 'number') {
            maxRow = Math.max(maxRow, u.row + u.h);
          }
        }
      } catch {}
    }

    // Fallback: scan children for initial positions (skip when column-based overrides exist,
    // as the legacy initial prop uses fixed breakpoint mappings that don't match per-column layouts)
    if (!hasColumnOverrides) {
      const scan = (nodes: React.ReactNode): void => {
        React.Children.forEach(nodes, (child) => {
          if (React.isValidElement(child)) {
            const props = child.props as any;
            if (props.initial && props.initial[breakpoint]) {
              const { row, h } = props.initial[breakpoint];
              maxRow = Math.max(maxRow, row + h);
            }
            if (props.children) scan(props.children);
          }
        });
      };
      scan(children);
    }
    return maxRow;
  }, [autoHeight, children, breakpoint, columns, layoutPath, layoutTick]);

  const rows = useMemo(() => {
    if (autoHeight) {
      // If minRows is provided, use those specific values
      if (minRows && minRows[breakpoint]) {
        return minRows[breakpoint];
      }
      
      // Content-based rows only; do not change with viewport height
      const contentRows = contentBasedRows || 5;
      return Math.max(1, contentRows);
    }
    if (containerHeight <= 0) return 10;
    return Math.max(1, Math.ceil(containerHeight / responsiveCellSize));
  }, [autoHeight, minRows, contentBasedRows, breakpoint, viewportHeight, containerHeight, responsiveCellSize]);

  // Track global rows delta so all items can react in sync
  const prevRowsRef = useRef<number>(rows);
  const isFirstRowsCalc = useRef<boolean>(true);
  useEffect(() => {
    if (isFirstRowsCalc.current) {
      isFirstRowsCalc.current = false;
      prevRowsRef.current = rows;
      setRowsDelta(0);
      return;
    }
    const delta = rows - prevRowsRef.current;
    setRowsDelta(delta);
    prevRowsRef.current = rows;
  }, [rows]);

  const toGrid = useCallback((px: number) => Math.round(px / responsiveCellSize), [responsiveCellSize]);
  const toPx = useCallback((units: number) => units * responsiveCellSize, [responsiveCellSize]);

  const nextZIndex = useCallback(() => {
    setZCounter((z) => z + 1);
    return zCounter + 1;
  }, [zCounter]);

  // Register/unregister items so we can collect and save layouts
  const registerItem = useCallback((id: string, getLayout: () => Record<BreakpointKey, UnitsRect>, setLayoutFor: (bp: BreakpointKey, rect: UnitsRect) => void) => {
    registryRef.current.set(id, { getLayout, setLayoutFor });
    // Apply any pending override for current breakpoint
    try {
      const overrides = pendingOverridesRef.current[breakpoint];
      const rect = overrides[id];
      if (rect) setLayoutFor(breakpoint, rect);
    } catch {}
  }, [breakpoint]);

  const unregisterItem = useCallback((id: string) => {
    registryRef.current.delete(id);
  }, []);

  const saveLayoutDebounced = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    saveTimerRef.current = window.setTimeout(async () => {
      // Determine current breakpoint, allowing debug override
      let bp: BreakpointKey = breakpoint;
      try {
        if (typeof window !== 'undefined' && (window as any).__debugBP) {
          const v = String((window as any).__debugBP).toLowerCase();
          if (v === 'mobile' || v === 'tablet' || v === 'desktop') bp = v as BreakpointKey;
        }
      } catch {}

      const arr = Array.from(registryRef.current.entries()).map(([id, entry]) => {
        const byBp = entry.getLayout();
        const u = byBp[bp] || byBp.desktop || byBp.tablet || byBp.mobile;
        return { id, col: u.col, row: u.row, w: u.w, h: u.h };
      });

      // Check if using column-based layouts (layoutPath ends with /layouts)
      const isColumnBased = layoutPath?.endsWith('/layouts');
      const saveKey = isColumnBased ? `layout-col-${columns}` : `layout.${bp}`;

      // localStorage fallback
      try { localStorage.setItem(saveKey, JSON.stringify(arr)); } catch {}

      // dev-only API write
      if (layoutPath) {
        try {
          const qs = isColumnBased 
            ? new URLSearchParams({ columns: String(columns), file: layoutPath })
            : new URLSearchParams({ bp, file: layoutPath });
          await fetch(`/api/layout?${qs.toString()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(arr)
          });
        } catch {}
      }
    }, 120);
  }, [breakpoint, layoutPath, columns]);

  // Save all items immediately (manual save)
  const saveAll = useCallback(async () => {
    console.log('[GridContent saveAll] 💾 Starting save...');
    
    // Determine current breakpoint, allowing debug override
    let bp: BreakpointKey = breakpoint;
    try {
      if (typeof window !== 'undefined' && (window as any).__debugBP) {
        const v = String((window as any).__debugBP).toLowerCase();
        if (v === 'mobile' || v === 'tablet' || v === 'desktop') bp = v as BreakpointKey;
      }
    } catch {}

    console.log('[GridContent saveAll] Registry size:', registryRef.current.size);
    
    const arr = Array.from(registryRef.current.entries()).map(([id, entry]) => {
      const byBp = entry.getLayout();
      console.log(`[GridContent saveAll] Item ${id} layout:`, byBp);
      const u = byBp[bp] || byBp.desktop || byBp.tablet || byBp.mobile;
      return { id, col: u.col, row: u.row, w: u.w, h: u.h };
    });

    console.log('[GridContent saveAll] Items to save:', arr.length);
    console.log('[GridContent saveAll] Full array:', JSON.stringify(arr, null, 2));
    console.log('[GridContent saveAll] Current columns:', columns);
    console.log('[GridContent saveAll] layoutPath:', layoutPath);

    // VISUAL DEBUG: Show what we're about to save
    const debugInfo = arr.map(item => `${item.id}: col=${item.col} row=${item.row}`).join('\n');
    alert(`💾 Saving ${arr.length} items to col-${columns}.json:\n\n${debugInfo}\n\nCheck console for full details (enable "Preserve log"!)`);

    // Check if using column-based layouts (layoutPath ends with /layouts)
    const isColumnBased = layoutPath?.endsWith('/layouts');
    const saveKey = isColumnBased ? `layout-col-${columns}` : `layout.${bp}`;

    console.log('[GridContent saveAll] isColumnBased:', isColumnBased);
    console.log('[GridContent saveAll] saveKey:', saveKey);

    // Save to localStorage FIRST before making API call
    try { 
      localStorage.setItem(saveKey, JSON.stringify(arr)); 
      console.log('[GridContent saveAll] ✅ Saved to localStorage:', saveKey);
    } catch (e) {
      console.error('[GridContent saveAll] ❌ Failed to save to localStorage:', e);
    }
    
    if (layoutPath) {
      try {
        const qs = isColumnBased 
          ? new URLSearchParams({ columns: String(columns), file: layoutPath })
          : new URLSearchParams({ bp, file: layoutPath });
        
        const url = `/api/layout?${qs.toString()}`;
        console.log('[GridContent saveAll] 📤 Sending to:', url);
        console.log('[GridContent saveAll] 📦 Payload:', arr);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(arr)
        });
        
        console.log('[GridContent saveAll] ✅ Response status:', response.status);
        const result = await response.json();
        console.log('[GridContent saveAll] ✅ Response:', result);
        
        // Force a hard reload to clear module cache and reload JSON files
        console.log('[GridContent saveAll] 🔄 Forcing hard reload to load new JSON files...');
        setTimeout(() => {
          window.location.reload();
        }, 500);
        
        setDirty(false);
      } catch (err) {
        console.error('[GridContent saveAll] ❌ Error:', err);
      }
    } else {
      setDirty(false);
    }
  }, [breakpoint, layoutPath, columns]);

  // Mark unsaved changes
  const markDirty = useCallback(() => {
    console.log('[GridContent] ⚠️ Item changed - mark as dirty (manual save required)');
    setDirty(true);
    // REMOVED auto-save - let user click Save button manually
  }, []);

  // Load initial layout for current breakpoint from JSON file and localStorage
  const loadInitialLayout = useCallback(async (bp: BreakpointKey) => {
    // Skip if using column-based layouts (those use loadColumnLayout instead)
    if (layoutPath && layoutPath.endsWith('/layouts')) return;
    
    const overrides: Record<string, UnitsRect> = {};
    // Try JSON file via fetch to avoid module import 404 noise (only when layoutPath is provided)
    if (layoutPath) {
      try {
        // In production, files are served from root, not /src/
        const jsonPath = import.meta.env.PROD 
          ? `/${layoutPath}.${bp}.json`
          : `/src/${layoutPath}.${bp}.json`;
        const res = await fetch(jsonPath, { cache: 'no-cache' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            for (const it of data) {
              if (it && typeof it.id === 'string' && typeof it.col === 'number') {
                overrides[it.id] = { col: it.col, row: it.row, w: it.w, h: it.h };
              }
            }
          }
        }
      } catch {}
    }
    // Fallback to localStorage
    try {
      const saved = localStorage.getItem(`layout.${bp}`);
      if (saved) {
        const data = JSON.parse(saved);
        if (Array.isArray(data)) {
          for (const it of data) {
            if (it && typeof it.id === 'string' && typeof it.col === 'number') {
              overrides[it.id] = { col: it.col, row: it.row, w: it.w, h: it.h };
            }
          }
        }
      }
    } catch {}

    // Store overrides and apply to registered items
    pendingOverridesRef.current[bp] = overrides;
    for (const [id, entry] of registryRef.current.entries()) {
      const rect = overrides[id];
      if (rect) entry.setLayoutFor(bp, rect);
    }
  }, [layoutPath]);

  // Load column-based layout (components using /layouts) for the current column count
  const loadColumnLayout = useCallback(async () => {
    if (!layoutPath || !layoutPath.endsWith('/layouts')) return;
    const bp: BreakpointKey = breakpoint;
    const overrides: Record<string, UnitsRect> = {};
    const foundIds = new Set<string>();
    // Helper to fetch a column JSON and return a parsed array or null
    const fetchCol = async (n: number): Promise<any[] | null> => {
      const clamped = Math.max(4, Math.min(20, n)); // Support 4-20 columns
      try {
        // In production, files are served from root, not /src/
        const jsonPath = import.meta.env.PROD
          ? `/${layoutPath}/col-${clamped}.json`
          : `/src/${layoutPath}/col-${clamped}.json`;
        const res = await fetch(jsonPath, { cache: 'no-cache' });
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return null;
        return data;
      } catch { return null; }
    };

    // Try exact columns first, then search nearest neighbors
    let data: any[] | null = await fetchCol(columns);
    if (!data) {
      for (let d = 1; d <= 5 && !data; d++) {
        data = await fetchCol(columns + d) || await fetchCol(columns - d);
      }
    }
    if (data) {
      for (const it of data) {
        if (it && typeof it.id === 'string' && typeof it.col === 'number') {
          overrides[it.id] = { col: it.col, row: it.row, w: it.w, h: it.h };
          foundIds.add(it.id);
        }
      }
    }

    // Fallback to column-based localStorage cache (used only for ids missing from JSON)
    try {
      const saved = localStorage.getItem(`layout-col-${columns}`);
      if (saved) {
        const data = JSON.parse(saved);
        if (Array.isArray(data)) {
          for (const it of data) {
            if (it && typeof it.id === 'string' && typeof it.col === 'number' && !foundIds.has(it.id)) {
              // only use localStorage if JSON didn't provide this id
              overrides[it.id] = { col: it.col, row: it.row, w: it.w, h: it.h };
            }
          }
        }
      }
    } catch {}

    // Apply overrides for current breakpoint only (ItemDrag stores per-bp state)
    pendingOverridesRef.current[bp] = overrides;
    for (const [id, entry] of registryRef.current.entries()) {
      const rect = overrides[id];
      if (rect) entry.setLayoutFor(bp, rect);
    }
    // Trigger rows recomputation for autoHeight
    setLayoutTick((t) => t + 1);
  }, [layoutPath, columns, breakpoint]);

  // Load initial layout for current breakpoint on mount (only for breakpoint-based layouts, not column-based)
  useEffect(() => {
    // Only load breakpoint-based layouts if NOT using column-based layouts
    if (layoutPath && !layoutPath.endsWith('/layouts')) {
      loadInitialLayout(breakpoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload layout when breakpoint changes (ensures per-BP JSON is applied) - only for breakpoint-based layouts
  useEffect(() => {
    // Only load breakpoint-based layouts if NOT using column-based layouts
    if (layoutPath && !layoutPath.endsWith('/layouts')) {
      loadInitialLayout(breakpoint);
    }
  }, [breakpoint, loadInitialLayout, layoutPath]);

  // Load column-based JSON whenever column count changes (for layoutPath ending with /layouts)
  useEffect(() => {
    loadColumnLayout();
  }, [loadColumnLayout]);

  // Dev: nothing extra; Vite plugin triggers full-reload after writes, and we fetch JSON on load

  const backgroundStyle: React.CSSProperties = useMemo(() => ({
    backgroundImage: showGuides ? `
      linear-gradient(to right, rgba(0,0,0,.15) 1px, transparent 0.5px),
      linear-gradient(to bottom, rgba(0,0,0,.15) 1px, transparent 0.5px)
    ` : undefined,
    backgroundSize: showGuides ? `${responsiveCellSize}px ${responsiveCellSize}px` : undefined,
    backgroundColor: 'transparent'
  }), [responsiveCellSize, showGuides]);

  const value = useMemo<GridContentContextValue>(() => ({
    cellSize: responsiveCellSize,
    columns,
    rows,
    rowsDelta,
    editMode,
    setEditMode: wrappedSetEditMode,
    toGrid,
    toPx,
    boardRef,
    activeId,
    setActiveId,
    nextZIndex,
    breakpoint,
    debug,
    registerItem,
    unregisterItem,
    saveLayoutDebounced,
    markDirty,
    saveAll
  }), [responsiveCellSize, columns, rows, rowsDelta, editMode, wrappedSetEditMode, toGrid, toPx, activeId, nextZIndex, breakpoint, debug, registerItem, unregisterItem, saveLayoutDebounced, markDirty, saveAll]);

  const handleBoardPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!editMode) return;
    const widgetEl = (e.target as HTMLElement).closest('[data-itemdrag="true"]');
    if (!widgetEl) setActiveId(null);
  }, [editMode]);

  const boardClasses = useMemo(() => {
    const alignment = fullBleed ? 'justify-start items-start' : 'justify-center items-center';
    const padding = noPadding ? '' : 'px-16';
    const centering = fullBleed ? '' : 'mx-auto';
    const base = [
      'relative w-full h-full flex', alignment, centering, padding,
      showGuides ? 'edit shadow-[inset_0_0_0_1px_#00000020]' : ''
    ].filter(Boolean).join(' ');
    return className ? `${base} ${className}` : base;
  }, [className, showGuides, fullBleed, noPadding]);

  const onPanelPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    panelDragging.current = true;
    panelPointerId.current = e.pointerId;
    panelStart.current = { x: e.clientX, y: e.clientY, left: panelPos.x, top: panelPos.y };
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  }, [panelPos]);

  const onPanelPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!panelDragging.current || panelPointerId.current !== e.pointerId || !panelStart.current) return;
    e.preventDefault();
    const dx = e.clientX - panelStart.current.x;
    const dy = e.clientY - panelStart.current.y;
    setPanelPos({ x: panelStart.current.left + dx, y: panelStart.current.top + dy });
  }, []);

  const onPanelPointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (panelPointerId.current !== e.pointerId) return;
    panelDragging.current = false;
    panelPointerId.current = null;
    panelStart.current = null;
    try { (e.currentTarget as Element).releasePointerCapture(e.pointerId); } catch {}
  }, []);

  const colsRangeLabel = useMemo(() => {
    if (breakpoint === 'mobile') return '5 (≤600px)';
    if (breakpoint === 'tablet') return '9|13 (601-1024px)';
    return '14–20 (≥1025px)'; // desktop
  }, [breakpoint]);

  // Calculate dynamic width based on complete columns only
  const boardStyle = useMemo(() => {
    // Set exact width to show only complete grid cells, unless fullBleed
    const exactGridWidth = columns * responsiveCellSize;
    const exactGridHeight = rows * responsiveCellSize;

    return {
      ...backgroundStyle,
      width: fullBleed ? '100%' : `${exactGridWidth}px`,
      height: `${exactGridHeight}px`,
      marginLeft: fullBleed ? undefined : 'auto',
      marginRight: fullBleed ? undefined : 'auto',
      // Ensure no overflow of partial cells
      overflow: 'visible'
    } as React.CSSProperties;
  }, [backgroundStyle, columns, rows, responsiveCellSize, fullBleed]);

  return (
    <GridContentContext.Provider value={value}>
      <div ref={boardRef} className={boardClasses} style={boardStyle} onPointerDown={handleBoardPointerDown}>
        {/* Debug info when guide is enabled */}
        {showGuides && isPanelOwner && panelMounted && createPortal(
          <div
            ref={panelRef}
            className="fixed bg-black/60 text-white rounded-md text-xs font-[600] border border-white/20 shadow-xl"
            style={{ left: panelPos.x, top: panelPos.y, zIndex: 2147483647, backdropFilter: 'blur(2px)' }}
          >
            <div
              className="px-2 py-1 select-none"
              onPointerDown={onPanelPointerDown}
              onPointerMove={onPanelPointerMove}
              onPointerUp={onPanelPointerEnd}
              onPointerCancel={onPanelPointerEnd}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(columns, 8)}, minmax(0,1fr))`,
                gap: '8px',
              }}
            >
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Cols</div>
                <div className="font-[700]">{columns}</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Rows</div>
                <div>{rows}</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Scrn</div>
                <div>{viewportWidth}px</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Grid W.</div>
                <div>{containerWidth}px</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Cell</div>
                <div>{responsiveCellSize}px</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">Mode</div>
                <div className="font-[700]">{breakpoint}</div>
              </div>
              <div className="text-center px-1 border-r border-white/20">
                <div className="text-[10px] opacity-50">M.Cols </div>
                <div className="text-[10px]">{colsRangeLabel}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] opacity-50">(E)dit</div>
                <div className={`font-[700] ${editMode ? 'text-[var(--GreenBrand-br-3)]' : 'text-[var(--RedBrand-br-3)]'}`}>
                  {editMode ? 'ON' : 'OFF'}
                </div>
              </div>
              <div className="col-span-full flex gap-2 items-center justify-between">
                <button
                  className={`px-1 rounded  leading-[1.5em] hover:bg-[var(--GreenBrand-br-3)] text-white text-[10px] font-[600] transition-colors ${dirty ? 'bg-[var(--GreenBrand-br-1)]' : 'opacity-50 bg-transparent cursor-not-allowed'}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); if (dirty) saveAll(); }}
                  title={dirty ? 'Save layout' : 'No unsaved changes'}
                >
                  Save
                </button>
                <button
                  className="px-1 rounded leading-[1.5em] hover:bg-red-500 bg-red-600 text-white text-[10px] font-[600] transition-colors"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm('Clear ALL localStorage layout data and reload?')) {
                      const keys = Object.keys(localStorage).filter(k => k.startsWith('layout-col-') || k.startsWith('grid-layout-'));
                      keys.forEach(k => localStorage.removeItem(k));
                      console.log('[GridContent] Cleared localStorage keys:', keys);
                      window.location.reload();
                    }
                  }}
                  title="Clear all cached layouts"
                >
                  Clear Cache
                </button>
                <span className="font-[500] font-main text-[10px] opacity-50">{dirty ? 'Unsaved changes' : 'Up to date'}</span>
              </div>
            </div>
          </div>,
          document.body
        )}
        {children}
      </div>
    </GridContentContext.Provider>
  );
};

export default GridContent;


